// hooks/useDeleteTransaction.js
// Módulo de eliminación atómica de transacciones - Stratium GLOBAL AI v3.4

import { useCallback } from 'react';
import { db } from '../firebase';
import { 
  doc, 
  collection, 
  runTransaction, 
  serverTimestamp,
  query,
  where,
  getDocs
} from 'firebase/firestore';

export const useDeleteTransaction = (usuarioActual, puedeAccederAFuncion, formatearValor) => {
  
  const handleDelete = useCallback(async (id, movimientos, setMovimientos, setInventario, setUsuarioActual, setValidationMessage, setError, setLoading) => {
    if (!usuarioActual?.uid) {
      setError('No autenticado');
      return;
    }

    const registroAEliminar = movimientos.find(m => m.id === id);
    if (!registroAEliminar) {
      setError('Registro no encontrado');
      return;
    }

    // ✅ DETECTAR TIPO DE OPERACIÓN
   const esDevolucionCompra = registroAEliminar.tipoOperacion === 'devolucion_compra' || 
                           registroAEliminar.tipo === 'DEVOLUCION_COMPRA' ||
                           registroAEliminar.categoria === 'DEVOLUCION_COMPRA' ||
                           registroAEliminar.texto?.includes('Devolución en Compra') ||
                           registroAEliminar.texto?.includes('DEVOLUCION_COMPRA') ||
                           registroAEliminar.concepto?.includes('Devolución') ||
                           registroAEliminar.tipo === 'egreso' && 
                           registroAEliminar.categoria === 'COMPRA' &&
                           registroAEliminar.texto?.includes('Devolución');

    const esDevolucionVenta = registroAEliminar.tipoOperacion === 'devolucion_venta' || 
                              registroAEliminar.tipo === 'DEVOLUCION_VENTA' ||
                              registroAEliminar.categoria === 'DEVOLUCION_VENTA' ||
                              registroAEliminar.texto?.includes('Devolución en Venta');

    const esFondosPersonales = 
      registroAEliminar.fuentePago === 'personal' ||
      registroAEliminar.esAportePersonal === true ||
      (registroAEliminar.recomendacion && 
       (registroAEliminar.recomendacion.includes('El negocio te debe este dinero') ||
        registroAEliminar.recomendacion.includes('aporte de capital personal')));

    const esCapitalInyectadoDevolucion = 
      (esDevolucionCompra && registroAEliminar.fuentePago === 'personal') ||
      (esDevolucionCompra && registroAEliminar.esAportePersonal === true);

    const tipoTexto = esDevolucionCompra ? 'DEVOLUCIÓN DE COMPRA' :
                      esDevolucionVenta ? 'DEVOLUCIÓN DE VENTA' :
                      registroAEliminar.tipo === 'ingreso' ? 'Venta/Ingreso' : 
                      registroAEliminar.categoria === 'PRODUCCION' ? 'Producción' : 'Compra/Gasto';

    // ✅ CALCULAR CANTIDAD CORRECTA
    let cantidad = 1;
    if (esDevolucionCompra || esDevolucionVenta) {
      cantidad = registroAEliminar.cantidadDevuelta || 
                 registroAEliminar.cantidadDevolucion || 
                 registroAEliminar.cantidad || 1;
      
      if (cantidad > 10) {
        const texto = registroAEliminar.texto || registroAEliminar.concepto || '';
        const match = texto.match(/(\d+)x/);
        if (match) {
          const extraida = parseInt(match[1]);
          if (extraida > 0 && extraida <= 10) {
            cantidad = extraida;
          }
        }
      }
    } else {
      cantidad = registroAEliminar.cantidad || 1;
    }
    
    cantidad = Math.abs(cantidad);
    const valor = Math.abs(registroAEliminar.valor || 0);

    console.log('🔍 Eliminando:', { tipo: tipoTexto, cantidad, valor });

    const confirmMessage = `¿Eliminar este registro permanentemente?
━━━━━━━━━━━━━━━━━━━━━
📌 Concepto: ${registroAEliminar.concepto || registroAEliminar.productoDevuelto}
💰 Valor: ${formatearValor(valor)}
📦 Cantidad: ${cantidad}
🔄 Tipo: ${tipoTexto}
━━━━━━━━━━━━━━━━━━━━━
⚠️ Esta acción revertirá TODOS los efectos en:
• Saldo de caja
• Inventario (si aplica)
${esFondosPersonales || esCapitalInyectadoDevolucion ? '• Deuda con dueño y Aportes personales (Capital Inyectado)' : ''}
• Bitácora de auditoría

¿Deseas continuar?`;

    if (!window.confirm(confirmMessage)) return;

    setLoading(true);

    try {
      await runTransaction(db, async (transaction) => {
        // ============================================================
        // 1. LEER DOCUMENTOS ACTUALES
        // ============================================================
        
        const userRef = doc(db, 'usuarios', usuarioActual.uid);
        const userSnap = await transaction.get(userRef);
        if (!userSnap.exists()) {
          throw new Error('Usuario no encontrado en Firestore');
        }
        const userData = userSnap.data();
        let nuevoSaldo = userData.saldoCaja || 0;
        let nuevaDeuda = userData.deudaConDueño || 0;
        let nuevosAportes = userData.aportesPersonales || 0;

        console.log('📊 Estado actual:', { 
          saldo: nuevoSaldo, 
          deuda: nuevaDeuda, 
          aportes: nuevosAportes 
        });

        // ============================================================
        // 2. BUSCAR INVENTARIO
        // ============================================================
        
        let inventarioRef = null;
        let productoData = null;
        let nuevaCantidad = 0;

        const nombreProducto = registroAEliminar.productoDevuelto || registroAEliminar.concepto;

        if (nombreProducto) {
          const inventarioQuery = query(
            collection(db, 'inventario'),
            where('producto', '==', nombreProducto),
            where('userId', '==', usuarioActual.uid)
          );
          const snapshot = await getDocs(inventarioQuery);
          if (!snapshot.empty) {
            inventarioRef = snapshot.docs[0].ref;
            productoData = snapshot.docs[0].data();
            nuevaCantidad = productoData.cantidad || 0;
          }
        }

        // ============================================================
        // 3. CALCULAR REVERSIÓN
        // ============================================================
        
        let ajusteSaldo = 0;
        let ajusteInventario = 0;
        let ajusteDeuda = 0;
        let ajusteAportes = 0;
        let tipoMovimiento = '';

        // 🔥 CASO 1: DEVOLUCIÓN DE COMPRA
        if (esDevolucionCompra) {
          // La devolución ORIGINAL: 
          // - RESTÓ del inventario (-cantidad)
          // - SUMÓ al saldo (+valor) porque te devolvieron dinero
          // Al ELIMINAR:
          // - SUMAR al inventario (+cantidad)
          // - RESTAR del saldo (-valor)
          ajusteInventario = cantidad;
          ajusteSaldo = -valor;  // ✅ RESTAR del saldo
          
          console.log('🔄 Devolución Compra - Ajustes:', { 
            ajusteInventario, 
            ajusteSaldo,
            valor,
            cantidad 
          });
          
          if (esCapitalInyectadoDevolucion || esFondosPersonales) {
            ajusteDeuda = -valor;
            ajusteAportes = -valor;
          }
          
          tipoMovimiento = 'DEVOLUCION_COMPRA_ELIMINADA';
        }
        // 🔥 CASO 2: DEVOLUCIÓN DE VENTA
        else if (esDevolucionVenta) {
          // La devolución ORIGINAL: 
          // - SUMÓ al inventario (+cantidad) porque cliente devolvió
          // - RESTÓ del saldo (-valor) porque reembolsaste
          // Al ELIMINAR:
          // - RESTAR del inventario (-cantidad)
          // - SUMAR al saldo (+valor)
          ajusteInventario = -cantidad;
          ajusteSaldo = valor;  // ✅ SUMAR al saldo
          
          tipoMovimiento = 'DEVOLUCION_VENTA_ELIMINADA';
        }
        // 🔥 CASO 3: VENTA NORMAL
        else if (registroAEliminar.tipo === 'ingreso') {
          ajusteSaldo = -valor;
          ajusteInventario = cantidad;
          tipoMovimiento = 'VENTA';
        }
        // 🔥 CASO 4: COMPRA NORMAL
        else if (registroAEliminar.tipo === 'egreso') {
          if (registroAEliminar.categoria === 'PRODUCCION') {
            ajusteSaldo = 0;
            ajusteInventario = -cantidad;
            tipoMovimiento = 'PRODUCCION';
          } else {
            ajusteSaldo = valor;
            ajusteInventario = -cantidad;
            tipoMovimiento = 'COMPRA';
            
            if (esFondosPersonales) {
              ajusteDeuda = -valor;
              ajusteAportes = -valor;
            }
          }
        }

        console.log('📊 Ajustes calculados:', { 
          ajusteSaldo, 
          ajusteInventario, 
          ajusteDeuda, 
          ajusteAportes 
        });

        // ============================================================
        // 4. VALIDACIONES
        // ============================================================
        
        if (inventarioRef && ajusteInventario !== 0) {
          const nuevaCantidadFinal = nuevaCantidad + ajusteInventario;
          if (nuevaCantidadFinal < 0) {
            throw new Error(`El inventario de "${nombreProducto}" quedaría negativo (${nuevaCantidadFinal}). Stock actual: ${nuevaCantidad}, Ajuste: ${ajusteInventario}`);
          }
        }

        if (ajusteSaldo !== 0) {
          const nuevoSaldoFinal = nuevoSaldo + ajusteSaldo;
          if (nuevoSaldoFinal < 0) {
            throw new Error(`El saldo quedaría negativo (${formatearValor(nuevoSaldoFinal)})`);
          }
        }

        if (ajusteDeuda !== 0) {
          const nuevaDeudaFinal = nuevaDeuda + ajusteDeuda;
          if (nuevaDeudaFinal < 0) {
            throw new Error(`La deuda quedaría negativa (${formatearValor(nuevaDeudaFinal)})`);
          }
        }

        // ============================================================
        // 5. ACTUALIZAR
        // ============================================================
        
        const updateData = {};
        if (ajusteSaldo !== 0) updateData.saldoCaja = nuevoSaldo + ajusteSaldo;
        if (ajusteDeuda !== 0) updateData.deudaConDueño = nuevaDeuda + ajusteDeuda;
        if (ajusteAportes !== 0) updateData.aportesPersonales = nuevosAportes + ajusteAportes;
        
        console.log('📝 Update data:', updateData);
        
        if (Object.keys(updateData).length > 0) {
          transaction.update(userRef, updateData);
        }

        if (inventarioRef && ajusteInventario !== 0) {
          const nuevaCantidadFinal = nuevaCantidad + ajusteInventario;
          const inventarioUpdate = {
            cantidad: nuevaCantidadFinal,
            fechaActualizacion: serverTimestamp()
          };
          
          if (nuevaCantidadFinal === 0) {
            inventarioUpdate.costoUnitario = 0;
            inventarioUpdate.costoTotal = 0;
          }
          
          transaction.update(inventarioRef, inventarioUpdate);
        }

        transaction.delete(doc(db, 'registros', id));

        if (puedeAccederAFuncion('puedeVerLogs')) {
          const logRef = doc(collection(db, 'logsEliminaciones'));
          transaction.set(logRef, {
            userId: usuarioActual.uid,
            userEmail: usuarioActual.email,
            registroId: registroAEliminar.id,
            concepto: nombreProducto,
            valor: valor,
            cantidad: cantidad,
            tipoOperacion: registroAEliminar.tipoOperacion || registroAEliminar.tipo,
            fechaEliminacion: serverTimestamp(),
            tipoMovimiento: tipoMovimiento,
            ajusteSaldo: ajusteSaldo,
            ajusteInventario: ajusteInventario
          });
        }
      });

      // ============================================================
      // 6. ACTUALIZAR UI
      // ============================================================
      
      setMovimientos(prev => prev.filter(m => m.id !== id));
      
      const nombreProducto = registroAEliminar.productoDevuelto || registroAEliminar.concepto;
      if (nombreProducto) {
        setInventario(prev => {
          const idx = prev.findIndex(i => i.producto === nombreProducto);
          if (idx >= 0) {
            const nuevo = [...prev];
            let ajuste = 0;
            
            if (esDevolucionCompra) ajuste = cantidad;
            else if (esDevolucionVenta) ajuste = -cantidad;
            else if (registroAEliminar.tipo === 'ingreso') ajuste = cantidad;
            else if (registroAEliminar.tipo === 'egreso') ajuste = -cantidad;
            
            const nuevaCantidadFinal = (nuevo[idx].cantidad || 0) + ajuste;
            nuevo[idx] = {
              ...nuevo[idx],
              cantidad: nuevaCantidadFinal,
              ...(nuevaCantidadFinal === 0 && { costoUnitario: 0, costoTotal: 0 })
            };
            return nuevo;
          }
          return prev;
        });
      }
      
      setUsuarioActual(prev => {
        const newState = { ...prev };
        if (ajusteSaldo !== 0) newState.saldoCaja = (prev?.saldoCaja || 0) + ajusteSaldo;
        if (ajusteDeuda !== 0) newState.deudaConDueño = (prev?.deudaConDueño || 0) + ajusteDeuda;
        if (ajusteAportes !== 0) newState.aportesPersonales = (prev?.aportesPersonales || 0) + ajusteAportes;
        return newState;
      });

      setValidationMessage(`✅ Registro eliminado exitosamente. Concepto: ${nombreProducto}`);
      setTimeout(() => setValidationMessage(null), 5000);

    } catch (error) {
      console.error('❌ Error:', error);
      setError(`❌ ${error.message}`);
      setTimeout(() => setError(null), 5000);
    } finally {
      setLoading(false);
    }
  }, [usuarioActual, puedeAccederAFuncion, formatearValor]);

  return { handleDelete };
};

export default useDeleteTransaction;

